import { defineStore } from "pinia";
import { ref, computed, watch, effectScope } from "vue";
import type { ZaptimeConfig } from "@zaptime/core";
import { languages } from "../languages";
import { useDebounceFn } from "@vueuse/core";
import axios from "axios";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { EventType } from "../types/EventType";
import useDays from "../useDays";

export const useEventTypeStore = defineStore("eventTypeStore", () => {
    const { days } = useDays();
    const eventType = ref<EventType>();
    const justSaved = ref(false);
    const saveFailed = ref(false);
    const autosaveStarted = ref(false);
    const isDirty = ref(false);
    let savedTimeout: ReturnType<typeof setTimeout> | null = null;
    let pendingExtra: Record<string, unknown> = {};
    let inFlightSave: Promise<void> | null = null;
    let lastSyncedSnapshot = "";

    const autosaveScope = effectScope(true);

    const imageUrl = ref(
        "https://images.pexels.com/photos/718978/pexels-photo-718978.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    );
    const visibleMonthsInThePast = ref(0);
    const visibleMonthsInTheFuture = ref(2);

    const token = ref<string>("");

    const theme = ref<ZaptimeConfig["theme"]>();
    const introductionText = ref("Meet with Jane");

    const chooseDateLocaleText = ref("Choose date");
    const choosePreferredTime = ref("Choose preferred time");

    const noEventAvailableLocaleText = ref(
        "There is no event available for chosen month.",
    );

    const redirectAfterBookingUrl = ref<string>();
    const calendarTitle = ref<string | undefined>(undefined);

    const pickTimeLocaleText = ref("Pick a Time");
    const confirmBookingLocaleText = ref("Confirm booking");
    const showNextMonth = ref("Show next month");

    const hideTimePreferences = ref(false);

    const confirmationButtonLocaleText = ref("Confirm booking");
    const goBackButtonLocaleText = ref("Go back");

    const confirmationRescheduleButtonText = ref("Reschedule");
    const reschedulingEventText = ref("Rescheduling an enent");
    const addGuestsLocaleText = ref("Add a guest");

    const compact = ref(false);

    const startDayOfTheWeek = ref(days.value[0]);
    const borderRadius = ref({
        radius: "0",
    });

    const selectedLanguage = ref(languages[0]);

    const config = computed((): ZaptimeConfig => {
        return {
            ...(import.meta.env.DEV && {
                apiBaseUrl: import.meta.env.VITE_API_URL,
            }),
            token: token.value,
            calendarTitle: calendarTitle.value,
            min: visibleMonthsInThePast.value,
            max: visibleMonthsInTheFuture.value,
            redirectAfterBookingUrl: redirectAfterBookingUrl.value,
            profileImage: imageUrl.value,
            externalBooking: false,
            compact: compact.value,
            locale: {
                preset: selectedLanguage.value.id,
                startDayOfWeek: startDayOfTheWeek.value.id,
                hideTimePreferences: hideTimePreferences.value,
                texts: {
                    chooseDate: chooseDateLocaleText.value,
                    noTimeSlotAvailable: noEventAvailableLocaleText.value,
                    introduction: introductionText.value,
                    pickTime: pickTimeLocaleText.value,
                    choosePreferredTime: choosePreferredTime.value,
                    showNextMonth: showNextMonth.value,
                },

                confirmationForm: {
                    confirmBooking: confirmBookingLocaleText.value,
                    reschedulingEvent: reschedulingEventText.value,
                    addGuests: addGuestsLocaleText.value,
                    buttons: {
                        confirmBooking: confirmationButtonLocaleText.value,
                        goBack: goBackButtonLocaleText.value,
                        reschedule: confirmationRescheduleButtonText.value,
                    },
                },
            },
            theme: theme.value,
        };
    });

    async function trackSave<T>(savePromise: Promise<T>): Promise<T> {
        if (savedTimeout) {
            clearTimeout(savedTimeout);
            savedTimeout = null;
        }

        justSaved.value = false;

        let saved = false;

        try {
            const result = await savePromise;
            saved = true;
            return result;
        } finally {
            if (saved) {
                justSaved.value = true;
                savedTimeout = setTimeout(() => {
                    justSaved.value = false;
                    savedTimeout = null;
                }, 3000);
            }
        }
    }

    function configSnapshot(): Record<string, unknown> {
        const confCopy = JSON.parse(JSON.stringify(config.value)) as Record<
            string,
            unknown
        >;

        delete confCopy.token;

        return confCopy;
    }

    function markSynced(): void {
        lastSyncedSnapshot = JSON.stringify(configSnapshot());
    }

    function isTransientSaveError(error: unknown): boolean {
        if (!axios.isAxiosError(error)) {
            return false;
        }

        if (error.response === undefined) {
            return true;
        }

        const status = error.response.status;

        return status >= 500 || status === 408 || status === 429;
    }

    const MAX_SAVE_ATTEMPTS = 3;

    async function patchWithRetry(
        eventTypeId: number,
        payload: Record<string, unknown>,
    ): Promise<unknown> {
        for (let attempt = 1; ; attempt++) {
            try {
                return await axios.patch(
                    route("app.event-types.patch", {
                        eventType: eventTypeId,
                    }),
                    payload,
                );
            } catch (error) {
                if (
                    attempt >= MAX_SAVE_ATTEMPTS ||
                    !isTransientSaveError(error)
                ) {
                    throw error;
                }

                await new Promise((resolve) =>
                    setTimeout(resolve, attempt * 1000),
                );
            }
        }
    }

    const debouncedFlush = useDebounceFn(() => {
        void flushSave();
    }, 500);

    function queueSave(extra?: Record<string, unknown>): void {
        if (extra !== undefined) {
            pendingExtra = { ...pendingExtra, ...extra };
        } else if (
            !isDirty.value &&
            JSON.stringify(configSnapshot()) === lastSyncedSnapshot
        ) {
            return;
        }

        isDirty.value = true;
        void debouncedFlush();
    }

    async function flushSave(): Promise<void> {
        if (
            inFlightSave !== null ||
            !isDirty.value ||
            eventType.value === undefined
        ) {
            return;
        }

        isDirty.value = false;

        const confCopy = configSnapshot();
        const snapshot = JSON.stringify(confCopy);
        const sentExtra = pendingExtra;
        const payload = { ...confCopy, ...sentExtra };
        const eventTypeId = eventType.value.id;

        pendingExtra = {};

        inFlightSave = (async () => {
            try {
                await trackSave(patchWithRetry(eventTypeId, payload));
                saveFailed.value = false;
                lastSyncedSnapshot = snapshot;
            } catch {
                saveFailed.value = true;
                pendingExtra = { ...sentExtra, ...pendingExtra };
                isDirty.value = true;
            } finally {
                inFlightSave = null;
            }
        })();

        await inFlightSave;

        if (isDirty.value && !saveFailed.value) {
            void debouncedFlush();
        }
    }

    function flushWithKeepalive(): void {
        if (!isDirty.value || eventType.value === undefined) {
            return;
        }

        const payload = { ...configSnapshot(), ...pendingExtra };
        const xsrfCookie = document.cookie.match(
            /(?:^|;\s*)XSRF-TOKEN=([^;]+)/,
        );

        void fetch(
            route("app.event-types.patch", {
                eventType: eventType.value.id,
            }),
            {
                method: "PATCH",
                keepalive: true,
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-XSRF-TOKEN": xsrfCookie
                        ? decodeURIComponent(xsrfCookie[1])
                        : "",
                },
                body: JSON.stringify(payload),
            },
        );

        isDirty.value = false;
    }

    const previewVersion = ref(0);

    function refreshPreview(): void {
        previewVersion.value += 1;
    }

    function startAutosave(): void {
        if (autosaveStarted.value) {
            return;
        }

        autosaveStarted.value = true;

        autosaveScope.run(() => {
            watch(config, () => {
                queueSave();
            });
        });

        if (typeof window !== "undefined") {
            router.on("before", () => {
                void flushSave();
            });

            window.addEventListener("pagehide", () => {
                flushWithKeepalive();
            });
        }
    }

    return {
        theme,
        config,
        justSaved,
        saveFailed,
        trackSave,
        queueSave,
        markSynced,
        startAutosave,
        introductionText,
        chooseDateLocaleText,
        choosePreferredTime,
        noEventAvailableLocaleText,
        pickTimeLocaleText,
        confirmBookingLocaleText,
        hideTimePreferences,
        confirmationButtonLocaleText,
        goBackButtonLocaleText,
        compact,
        startDayOfTheWeek,
        selectedLanguage,
        borderRadius,
        visibleMonthsInThePast,
        visibleMonthsInTheFuture,
        token,
        imageUrl,
        eventType,
        reschedulingEventText,
        confirmationRescheduleButtonText,
        addGuestsLocaleText,
        redirectAfterBookingUrl,
        showNextMonth,
        calendarTitle,
        previewVersion,
        refreshPreview,
    };
});
